<app-side-dialog
  #dialog
  [headerGlyph]="[$any('workspace'), $any('yaml')]"
  [headerText]="
    isEdit
      ? 'Edit with YAML: ' + (resource.displayName || resource.type)
      : 'New Resource - Create with YAML'
  "
  [saveText]="isEdit ? 'Save' : 'Create'"
  [busy]="loading"
  [isExpanded]="isDialogExpanded"
  [body]="body"
  [validationFn]="validateForm.bind(this)"
  (save)="onSubmit()"
  [contentVisible]="!loading"
  [errorText]="errorText"
  [errorMsg]="errorMsg"
  [additionalHeaderButtons]="expandBtn"
  [additionalErrorContent]="additionalErrorContent"
>
</app-side-dialog>
<ng-template #expandBtn>
  <fd-button-bar
    [ngClass]="{ 'expand-btn-expanded': isDialogExpanded }"
    class="expand-btn"
    [title]="!isDialogExpanded ? 'Full Screen' : 'Exit Full Screen'"
    [fdCompact]="true"
    [fdType]="isDialogExpanded ? 'transparent' : 'emphasized'"
    (click)="onToggleDialogExpand()"
  >
    <app-icon
      [icon]="[
        $any('workspace'),
        $any(!isDialogExpanded ? 'expand' : 'collapse'),
      ]"
      size="lg"
    ></app-icon>
  </fd-button-bar>
</ng-template>

<ng-template #body>
  <div class="wrapper">
    @if (!isEdit) {
      <div class="yaml-header-container">
        <label>
          Create one or more resources in the current path based on their
          required specification in YAML format@if (!Utils.isPrivateCloud) {<ng-container>. Go to the
          <a
            fd-link
            target="_blank"
            href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/"
            >Reference Guide</a
          >
          for resource definitions</ng-container>}.
        </label>
        <app-helpful-label
          i18n-label="
            XFLD|Label of the Generic Resource creation@@genericCreation.label"
          i18n-popoverTitle="
            XTIT|Title of the Generic Resource creation
            popover@@genericCreation.popover.title"
          popoverTitle="New Resource - Create with YAML"
          [popoverBody]="genericResourceCreatePopover"
          width="35rem"
        >
        </app-helpful-label>
        <ng-template
          #genericResourceCreatePopover
          i18n="
            XMSG|Body text of the Generic resource creation
            popover@@create.genericResource.popover.body"
        >
          Use this option to create one or more resources in the unified
          services framework by uploading or entering the resources' required
          specification in YAML format. You can either upload the YAML
          configuration by file, or you can paste or type it directly into the
          editor.
          <ul>
            <li>
              The resource types you are creating must be supported by the
              unified services framework.
            </li>
            <li>
              You cannot use this option to create folders or resource groups.
              Use instead the New > Folder and New > Resource Group options.
            </li>
            <li>
              The new resources must be created in the current level of the
              account hierarchy. Therefore, the metadata.path must specify the
              level you are currently in.
            </li>
            <li>
              Your user must have permission to create the specified resource
              types in the current level in the account hierarchy.
            </li>
            <li>
              You can include multiple resources in a single YAML. Make sure you
              separate each resource with a line containing a triple-dash
              separator ---.
            </li>
            <li>
              When uploading a file, the file must have the .yaml or .yml
              extension.
            </li>
            <li>
              You can upload multiple YAML files at the same time or one after
              another. If there is existing code in the editor, you will be
              prompted to either replace it or append the new YAML content to
              it.
            </li>
          </ul>
          @if (!Utils.isPrivateCloud) {
            <ng-container>
              For information about the specifications that define each of the
              supported resource types and their properties, including code
              samples, go to the
              <a
                fd-link
                target="_blank"
                href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/"
                >Unified Services Reference Guide</a
              >.
            </ng-container>
          }
        </ng-template>
      </div>
    }
    @if (!isEdit) {
      <div class="current-path-row">
        <label fd-form-label>Current Path:</label>
        <label fd-form-label>{{ currentPath }}</label>
        <button
          fd-button
          (click)="copyPath()"
          class="link-color"
          fdType="transparent"
          title="Copy path to clipboard"
        >
          <app-icon icon="copy" size="lg"></app-icon>
        </button>
      </div>
    }

    @if (isEdit) {
      <div>
        <div class="yaml-header-container">
          <label>
            Edit this resource based on its required specification in YAML
            format@if (!Utils.isPrivateCloud) {<ng-container>. Go to the
            <a
              fd-link
              target="_blank"
              href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/"
              >Reference Guide</a
            >
            for resource definitions</ng-container>}.
          </label>
          <app-helpful-label
            i18n-label="
              XFLD|Label of the Generic Resource Update@@genericUpdate.label"
            i18n-popoverTitle="
              XTIT|Title of the Generic Resource update
              popover@@genericUpdate.popover.title"
            popoverTitle="Edit Resource with YAML"
            [popoverBody]="genericResourceUpdatePopover"
            width="35rem"
          >
          </app-helpful-label>
        </div>
        <ng-template
          #genericResourceUpdatePopover
          i18n="
            XMSG|Body text of the Generic resource update
            popover@@update.genericResource.popover.body"
        >
          Use this option to update the resource by editing its required
          specification in YAML format.
          <br /><br />
          When you open the resource, its current YAML specification is
          displayed in the editor. You can edit it by either uploading an
          updated YAML configuration as a file, or you can paste or edit it
          directly in the editor.
          <ul>
            <li>
              Do not change the values of these attributes: apiVersion, type,
              metadata.name, metadata.path
            </li>
            <li>
              The YAML must only refer to the resource you are editing. You
              cannot add additional resources to the YAML.
            </li>
            <li>
              When uploading a file, the file must have the .yaml or .yml
              extension.
            </li>
          </ul>
          @if (!Utils.isPrivateCloud) {
            <ng-container>
              For information about the specifications that define each of the
              supported resource types and their properties, including code
              samples, go to the
              <a
                fd-link
                target="_blank"
                href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/reference-guide/"
                >Unified Services Reference Guide</a
              >.
            </ng-container>
          }
        </ng-template>
      </div>
    }

    <app-container [body]="basicInfoContainer"></app-container>

    <ng-template #basicInfoContainer>
      <div class="yaml-wrapper">
        <div>
          @if (!isEdit) {
            <div class="label-help-row">
              <label fd-form-label for="yaml-file-uploader" class="label-item"
                >Upload YAML from file:</label
              >
              <app-help-popover
                width="38rem"
                class="help-popover-right"
                linkText="Creating more than one resource?"
                [body]="tmp"
                [linkEmphasized]="false"
                placement="top"
              ></app-help-popover>
            </div>
            <ng-template #tmp>
              When generating multiple resources within a single YAML, please
              consider the following guidelines:
              <ul>
                <li>
                  Each resource should be separated by a line containing a
                  triple-dash separator (---).
                </li>
                <li>
                  Multiple YAML files can be uploaded simultaneously or
                  sequentially. If code already exists in the editor, you'll be
                  prompted to either replace it or append the new YAML content.
                </li>
                <li>
                  Ensure that any uploaded files have a .yaml or .yml extension.
                </li>
              </ul>
            </ng-template>
            <div class="uploader-container">
              <div>
                <fd-file-uploader
                  #uploadFile
                  id="yaml-file-uploader"
                  class="browse-yaml-button"
                  ariaLabel="Choose file"
                  ariaLabelledBy="Choose file"
                  i18n-placeholder="
                    XMSG|Placeholder of the Select File
                    input@@create.file.uploadFilePlaceholder"
                  i18n-buttonLabel="
                    XBUT|Text of the Select File
                    button@@create.file.uploadButtonText"
                  placeholder="Select one or more files"
                  buttonLabel="Browse..."
                  buttonAriaLabel="browse file"
                  accept=".yaml, .yml"
                  (selectedFilesChanged)="handleFileSelection($event)"
                >
                </fd-file-uploader>
              </div>
            </div>
          }
          @if (!isEdit) {
            <label class="enter-yaml-editor" fd-form-label
              >Or enter the YAML code directly into the editor:</label
            >
          }
          <div class="clear-button">
            <div>
              <button
                fd-button
                (click)="clearEditor()"
                id="clear-editor-btn"
                title="Clear"
                fdType="transparent"
                i18n-label="XMIT|Clear action in code editor@@code.editor.clear"
                label="Clear"
              ></button>
              <button
                fd-button
                [fdCompact]="true"
                [fdType]="'transparent'"
                class="custom-icon"
                (click)="onExpandYaml()"
                label="Expand"
                title="Expand YAML"
              ></button>
              <button
                fd-button
                [fdCompact]="true"
                [fdType]="'transparent'"
                class="custom-icon"
                (click)="onCollapseYaml()"
                label="Collapse"
                title="Collapse YAML"
              ></button>
            </div>
          </div>
        </div>

        <div class="yaml-editor-container" id="app-editor-edit">
          <app-editor
            #editorEdit
            [(text)]="yamlInputData"
            [class]="
              !!errorMsg
                ? 'generic-yaml app-ace-editor-error'
                : 'generic-yaml app-ace-editor-no-error'
            "
            [theme]="theme"
            [errorMsg]="errorMsg"
            (textChange)="onTextChange($event)"
            mode="yaml"
            [(valid)]="valid"
            (documentCountChange)="onDocumentCountChange($event)"
          ></app-editor>
        </div>
      </div>
    </ng-template>
  </div>
  @if (!errorMsg) {
    <div class="separator"></div>
  }
</ng-template>

<ng-template #additionalErrorContent>
  @if (errorType === ErrorType.WrongPath) {
    <div class="fix-path-wrapper">
      <button
        fd-button
        [fdCompact]="true"
        fdType="emphasized"
        i18n-label="
          XMSG|Fix Path button
          label@@organizations.dialogs.create-generic-resource-dialog.fixPathButtonLabel"
        [label]="multiDocumentMode ? 'FIX PATHS' : 'FIX PATH'"
        (click)="onFixPathClick()"
      ></button>
    </div>
  }
</ng-template>
